import { useRef, useEffect, ChangeEvent, MouseEvent, useReducer, useCallback } from 'react'
import { ChevronLeft, ChevronRight, SyncAlt } from '@mui/icons-material'
import { Button, IconButton, InputAdornment, TextField } from '@mui/material'
import { AnimatePresence } from 'framer-motion'
import debounce from 'lodash/debounce'
import CreatableSelect from 'react-select/creatable'
import { ButtonWithOverlay } from '@components/buttons/buttonWithOverlay'
import { useOverlay } from '@global/hooks/useOverlay'
import { pluralize } from '@utils/pluralize'
import { replaceOnString } from '@utils/replaceOnString'
import FindAndReplaceIcon from 'images/find_and_replace_icon.svg'
import { MatchCaseIcon } from './matchCaseIcon'
import { reducer, initialState } from './reducer'
import * as css from './styles'
import {
  ActionType,
  FindAndReplacePopoverProps,
  RecentChangesProps,
  Change,
  GroupedChanges,
  FooterProps,
  SelectProps,
} from './types'
import { t } from '@lingui/core/macro'

const RecentChanges = ({ recentChanges, undoChange }: RecentChangesProps) => {
  const groupedChanges: GroupedChanges = recentChanges.reduce((groups, change) => {
    const { previousTerm, newTerm } = change
    const key = `${previousTerm}-${newTerm}`
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(change)
    return groups
  }, {})

  const uniqueChanges = Object.values(groupedChanges)

  const animations = {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0, opacity: 0 },
    transition: { type: 'spring', stiffness: 500, damping: 40 },
  }

  return (
    <>
      <css.Subtitle>{t`Recent changes`}</css.Subtitle>
      <css.List>
        <AnimatePresence>
          {uniqueChanges.map(group => {
            const change = group[0]
            if (!change) {
              return null
            }

            const { previousTerm, newTerm } = change
            const key = `${previousTerm}-${newTerm}`
            return (
              <css.ListItem layout {...animations} key={key}>
                <css.Change>
                  <css.Term>{previousTerm}</css.Term>
                  <SyncAlt style={{ width: 18, height: 18, marginInline: 10, color: '#A9C5D0' }} />
                  <css.Term>{newTerm}</css.Term>
                </css.Change>
                <div>
                  <css.ChangesCount>
                    {pluralize(group.length, `Change`, `Changes`)}
                  </css.ChangesCount>
                  <Button
                    name="undo-changes"
                    variant="outlined"
                    css={css.UndoButton}
                    onClick={() => {
                      undoChange(group)
                    }}
                  >
                    UNDO
                  </Button>
                </div>
              </css.ListItem>
            )
          })}
        </AnimatePresence>
      </css.List>
    </>
  )
}

const Footer = ({ disableButtons, replaceSingleOccurence, replaceAllOccurrences }: FooterProps) => {
  return (
    <css.Footer>
      <Button
        name="replace"
        variant="outlined"
        css={css.ReplaceButton}
        onClick={replaceSingleOccurence}
        disabled={disableButtons}
      >
        {t`Replace`}
      </Button>
      <Button
        name="replace-all"
        variant="outlined"
        css={css.ReplaceAllButton}
        onClick={replaceAllOccurrences}
        disabled={disableButtons}
      >
        {t`Replace all`}
      </Button>
    </css.Footer>
  )
}

const selectComponents = {
  DropdownIndicator: null,
}

const CustomSelect = ({ onChange, listValues }: SelectProps) => {
  const options = listValues.map(item => ({
    label: item,
    value: item,
  }))

  return (
    <CreatableSelect
      components={selectComponents}
      isClearable
      onChange={onChange}
      styles={{
        control: (baseStyles, { isFocused }) => ({
          ...baseStyles,
          marginTop: '2px',
          marginBottom: '10px',
          borderColor: 'rgba(0,0,0,.67)',
          boxShadow: isFocused ? `0 0 0 1px rgba(0,0,0,.67)` : undefined,
          width: '250px',
          '&:hover': {
            borderColor: 'rgba(0,0,0,.67)',
          },
        }),
        option: (styles, { isFocused }) => {
          return {
            ...styles,
            backgroundColor: isFocused ? '#F0F9FF' : 'transparent',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: isFocused ? '#B3D9F4' : `#fff`,
            color: '#30505A',
          }
        },
      }}
      placeholder=""
      options={options}
      isValidNewOption={() => false}
      menuPosition="fixed"
    />
  )
}

export const FindAndReplacePopover = ({
  hotTable,
  colIdx,
  listValues,
}: FindAndReplacePopoverProps) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const { find, replace, matchCase, searchResults, selectedResultIndex, recentChanges } = state

  const buttonRef = useRef(null)
  const { anchorEl, onClick, onClose, open, id } = useOverlay()

  const handleClose = () => {
    dispatch({ type: ActionType.clearSearch })
    onClose(null, 'close')
  }

  useEffect(() => {
    if (!find) {
      dispatch({ type: ActionType.setFind, payload: '' })
      dispatch({ type: ActionType.setSearchResults, payload: [] })
      return
    }

    const search = hotTable.getPlugin('search')
    search.setQueryMethod((query, value) => {
      return matchCase
        ? value?.toString().includes(query)
        : value?.toString().toLowerCase().includes(query.toLowerCase())
    })

    const queryResult = search.query(find)
    const queryResultByColumn = queryResult.filter(result => result.col === colIdx)

    dispatch({ type: ActionType.setSearchResults, payload: queryResultByColumn })
  }, [find, matchCase, recentChanges])

  const debounceSetFind = useCallback(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: ActionType.setFind, payload: e.target.value })
    }, 300),
    [],
  )

  const debounceSetReplace = useCallback(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: ActionType.setReplace, payload: e.target.value })
    }, 300),
    [],
  )

  const handleClickMatchCase = () => {
    dispatch({ type: ActionType.toggleMatchCase })
  }
  const stopPropagation = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
  }

  const focusOnNextOcurrence = () => {
    const nextIndex = (selectedResultIndex + 1) % searchResults.length
    dispatch({ type: ActionType.setSelectedResultIndex, payload: nextIndex })
    const cell = searchResults[nextIndex]

    if (cell) {
      hotTable.selectCell(cell.row, colIdx)
      hotTable.scrollViewportTo(cell.row)
    }
  }

  const focusOnPreviousOcurrence = () => {
    const previousIndex = (selectedResultIndex - 1 + searchResults.length) % searchResults.length
    dispatch({ type: ActionType.setSelectedResultIndex, payload: previousIndex })

    const cell = searchResults[previousIndex]

    if (cell) {
      hotTable.selectCell(cell.row, colIdx)
      hotTable.scrollViewportTo(cell.row)
    }
  }

  const recordChange = ({ previousTerm, newTerm, rowIdx }) => {
    dispatch({
      type: ActionType.recordChange,
      payload: {
        previousTerm,
        newTerm,
        rowIdx,
      },
    })
  }

  const undoChange = useCallback((group: Change[]) => {
    hotTable.batch(() => {
      group.forEach(change => {
        const { rowIdx, previousTerm, newTerm } = change
        const cellValue = hotTable.getDataAtCell(rowIdx, colIdx)
        const revertedValue = replaceOnString(cellValue, newTerm, previousTerm, matchCase)

        hotTable.setDataAtCell(rowIdx, colIdx, revertedValue)
        dispatch({ type: ActionType.undoChange, payload: change })
      })
    })
  }, [])

  const replaceSingleOccurence = useCallback(() => {
    if (searchResults[selectedResultIndex]) {
      const { row, col, data } = searchResults[selectedResultIndex]
      const replacedValue = replaceOnString(data, find, replace, matchCase)
      const updatedSearchResults = searchResults.filter(result => result.row !== row)

      hotTable.setDataAtCell(row, col, replacedValue)
      recordChange({ previousTerm: data, newTerm: replacedValue, rowIdx: row })
      dispatch({ type: ActionType.setSearchResults, payload: updatedSearchResults })

      if (!updatedSearchResults.length) return

      const cell = updatedSearchResults[selectedResultIndex]
      if (cell) {
        hotTable.selectCell(cell.row, colIdx)
      }
    }
  }, [searchResults, selectedResultIndex, find, replace, matchCase])

  const replaceAllOccurrences = useCallback(() => {
    hotTable.batch(() => {
      searchResults.forEach(result => {
        const { row, col, data } = result
        const replacedValue = replaceOnString(data, find, replace, matchCase)
        hotTable.setDataAtCell(row, col, replacedValue)

        recordChange({ previousTerm: data, newTerm: replacedValue, rowIdx: row })
      })
    })
    dispatch({ type: ActionType.setSearchResults, payload: [] })
  }, [searchResults, find, replace, matchCase])

  const listValuesSelect = !hotTable.isDestroyed ? [...new Set(hotTable.getDataAtCol(colIdx))] : []

  return (
    <ButtonWithOverlay>
      <ButtonWithOverlay.Button
        popoverId="find-and-replace"
        ref={buttonRef}
        onClick={onClick}
        className={open ? 'open' : ''}
        extendStyles={css.Button}
      >
        <FindAndReplaceIcon />
      </ButtonWithOverlay.Button>
      <ButtonWithOverlay.Overlay
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          open,
          onClose: handleClose,
          anchorEl,
          id,
        }}
        sx={{
          '& .MuiPaper-root': {
            marginTop: '8px',
            padding: 0,
          },
        }}
      >
        <div
          style={{
            padding: '20px 21px',
          }}
        >
          <css.Title>{t`Find and replace`}</css.Title>
          <css.FindAndReplace>
            <div>
              <css.Label>{t`Find`}</css.Label>
              {listValues ? (
                <CustomSelect
                  onChange={newValue => {
                    dispatch({ type: ActionType.setFind, payload: newValue?.label || '' })
                  }}
                  listValues={listValuesSelect}
                />
              ) : (
                <TextField
                  style={{ marginTop: '2px', marginBottom: '10px' }}
                  name="find"
                  margin="dense"
                  variant="outlined"
                  onChange={debounceSetFind}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle case sensitivity"
                          onClick={handleClickMatchCase}
                          onMouseDown={stopPropagation}
                          edge="end"
                          disableRipple
                          css={css.MatchCaseButton}
                          size="large"
                        >
                          <span data-tooltip="Match Case" data-tooltip-dir="top">
                            <MatchCaseIcon on={matchCase} />
                          </span>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </div>
            <div>
              <css.Label>{t`Replace with`}</css.Label>
              {listValues ? (
                <CustomSelect
                  onChange={newValue => {
                    dispatch({ type: ActionType.setReplace, payload: newValue?.label || '' })
                  }}
                  listValues={listValues}
                />
              ) : (
                <TextField
                  style={{ marginTop: '2px', marginBottom: '10px' }}
                  name="replace"
                  margin="dense"
                  variant="outlined"
                  onChange={debounceSetReplace}
                  fullWidth
                />
              )}
            </div>
          </css.FindAndReplace>
          <css.Count>
            <IconButton
              aria-label="see previous occurrence"
              onClick={focusOnPreviousOcurrence}
              onMouseDown={stopPropagation}
              css={css.ChevronButton}
              disabled={!searchResults.length}
              size="large"
            >
              <ChevronLeft />
            </IconButton>
            <css.Found>
              {searchResults.length
                ? `${selectedResultIndex + 1} of ${searchResults.length} Found`
                : `No results found`}
            </css.Found>
            <IconButton
              aria-label="see next occurrence"
              onClick={focusOnNextOcurrence}
              onMouseDown={stopPropagation}
              css={css.ChevronButton}
              disabled={!searchResults.length}
              size="large"
            >
              <ChevronRight />
            </IconButton>
          </css.Count>

          {recentChanges.length ? (
            <RecentChanges recentChanges={recentChanges} undoChange={undoChange} />
          ) : null}
        </div>
        <Footer
          disableButtons={!replace}
          replaceSingleOccurence={replaceSingleOccurence}
          replaceAllOccurrences={replaceAllOccurrences}
        />
      </ButtonWithOverlay.Overlay>
    </ButtonWithOverlay>
  )
}
